import { motion, useReducedMotion } from "framer-motion";

/**
 * ERPStrategy — "Core Spine"
 * Five horizontal capability layers (Finance / Supply / Ops / Sales / HR) stacked
 * like the layers of a core platform. A vertical light spine pulses top→bottom
 * then bottom→top, sending small horizontal beams into each layer. Reflects:
 * the ERP as the spine that runs the entire business.
 */
const LAYERS = [
  { label: "FINANCE",    accent: "#93C5FD" },
  { label: "SUPPLY",     accent: "#7DB4FF" },
  { label: "OPERATIONS", accent: "#3B82F6" },
  { label: "SALES",      accent: "#7DB4FF" },
  { label: "HR",         accent: "#93C5FD" },
];

export default function CoreSpine({ className = "" }) {
  const reduce = useReducedMotion();
  const spineX = 210;
  const top = 30, bottom = 290;
  const rowH = (bottom - top) / LAYERS.length;

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <linearGradient id="spine-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="1" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="spine-row" x1="0" x2="1">
            <stop offset="0%" stopColor="#334155" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#334155" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Rows */}
        {LAYERS.map((row, i) => {
          const y = top + i * rowH + rowH / 2;
          return (
            <g key={row.label}>
              <rect x="30" y={y - rowH / 2 + 6} width="360" height={rowH - 12} fill="url(#spine-row)" opacity="0.35" />
              <line x1="30" y1={y} x2="380" y2={y} stroke="#334155" strokeOpacity="0.6" />
              <text x="42" y={y - 6} fill="#CBD5E1" fontSize="10" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
                {row.label}
              </text>
              {/* Anchor node on each row */}
              <circle cx="370" cy={y} r="3" fill={row.accent} />
              {/* Beam from spine to row anchor */}
              <motion.line
                x1={spineX} y1={y} x2="370" y2={y}
                stroke={row.accent}
                strokeOpacity="0.35"
                strokeWidth="1"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={reduce ? { pathLength: 1, opacity: 0.6 } : { pathLength: [0, 1, 1, 0], opacity: [0, 0.85, 0.85, 0] }}
                transition={reduce ? { duration: 0 } : { duration: 5, delay: 0.6 + i * 0.35, repeat: Infinity, ease: "easeInOut" }}
              />
            </g>
          );
        })}

        {/* Central vertical spine — the ERP core */}
        <line x1={spineX} y1={top - 4} x2={spineX} y2={bottom + 4} stroke="url(#spine-grad)" strokeWidth="2.5" />

        {/* Traveling pulse along the spine */}
        {!reduce && (
          <>
            <motion.circle
              cx={spineX} r="4" fill="#EFF6FF"
              initial={{ cy: top }}
              animate={{ cy: [top, bottom, top] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx={spineX} r="9" fill="#3B82F6" fillOpacity="0.28"
              initial={{ cy: top }}
              animate={{ cy: [top, bottom, top] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
          </>
        )}

        {/* Endcaps */}
        <circle cx={spineX} cy={top} r="4" fill="#93C5FD" />
        <circle cx={spineX} cy={bottom} r="4" fill="#93C5FD" />

        <text x={spineX} y="15" textAnchor="middle" fill="#CBD5E1" fontSize="9" letterSpacing="3" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          THE CORE
        </text>
      </svg>
    </div>
  );
}
